// Technical-Audit adapter for the shared <ConditionFilter>: the field config it
// shows, and compileConditions() which maps the generic conditions to this
// feature's backend query params. The generic model/UI lives in
// components/shared/ConditionFilter.

import type {
	FilterCondition,
	FilterFieldConfig,
	FieldOption,
} from "@/components/shared/ConditionFilter";

export interface CompiledFilter {
	includeTypes?: string[];
	excludeTypes?: string[];
	severity?: string;
	status?: string;
	search?: string;
}

/**
 * Compile AND-joined conditions to the technical-audit issues-filter params.
 * Same-field "is" rows union (OR via include list). Backend supports multi-value
 * for issue type only; severity/status/page take the first value (v1).
 */
export function compileConditions(conditions: FilterCondition[]): CompiledFilter {
	const out: CompiledFilter = {};
	const include: string[] = [];
	const exclude: string[] = [];

	for (const c of conditions) {
		const value = c.value?.trim();
		if (!value) continue;
		switch (c.field) {
			case "type":
				if (c.operator === "is_not") exclude.push(value);
				else include.push(value);
				break;
			case "severity":
				if (out.severity === undefined) out.severity = value;
				break;
			case "status":
				if (out.status === undefined) out.status = value;
				break;
			case "page":
				if (out.search === undefined) out.search = value;
				break;
		}
	}

	if (include.length) out.includeTypes = Array.from(new Set(include));
	if (exclude.length) out.excludeTypes = Array.from(new Set(exclude));
	return out;
}

/** Field config for the Technical Audit condition filter. */
export function buildAuditFilterFields(
	typeOptions: FieldOption[],
	severityOptions: FieldOption[],
	statusOptions: FieldOption[],
): FilterFieldConfig[] {
	const isOps = [
		{ value: "is", label: "is" },
		{ value: "is_not", label: "is not" },
	];
	return [
		{
			key: "type",
			label: "Issue type",
			operators: isOps,
			valueInput: {
				kind: "select",
				options: typeOptions,
				searchable: true,
				searchPlaceholder: "Search issue types...",
				emptyMessage: "No issue type found.",
			},
		},
		{ key: "severity", label: "Severity", operators: [{ value: "is", label: "is" }], valueInput: { kind: "select", options: severityOptions, searchable: true, searchPlaceholder: "Search severity...", emptyMessage: "No severity found." } },
		{ key: "status", label: "Status", operators: [{ value: "is", label: "is" }], valueInput: { kind: "select", options: statusOptions, searchable: true, searchPlaceholder: "Search status...", emptyMessage: "No status found." } },
		// Page URL search lives in the toolbar search box, not the condition builder.
	];
}
